import {
  faCircleNotch,
  faClose,
  faSave,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Button } from "../form/Button";

export const ModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ModalProviderContextProvider>
      <ModalOverlay />
      {children}
    </ModalProviderContextProvider>
  );
};

export function useModal(modal: Modal) {
  const modalContext = useContext(ModalProviderContext);

  const open = useCallback(
    (params?: string[]): void => {
      modalContext.open({
        ...modal,
        params: params !== undefined ? params : modal.params,
      });
    },
    [modal, modalContext.open],
  );

  return { open };
}

type Modal = {
  title: string;
  body: ReactNode | ((params?: string[]) => ReactNode);
  params?: string[];
  size?: "md" | "lg" | "xl" | "2xl";
};

interface ModalProviderContextType {
  modals: Modal[];
  close: () => void;
  open: (modal: Modal) => void;
}

const ModalProviderContext = createContext({} as ModalProviderContextType);

const ModalProviderContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<Modal[]>([]);
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModals((old) => old.slice(0, -1)); // Remove last item
      }
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  const close = useCallback(() => {
    setModals((old) => old.slice(0, -1)); // Remove last item
  }, []);

  const open = useCallback((modal: Modal) => {
    setModals((old) => [...old, modal]);
  }, []);

  return (
    <ModalProviderContext.Provider
      value={{
        modals: modals,
        close,
        open,
      }}
    >
      {children}
    </ModalProviderContext.Provider>
  );
};

const ModalOverlay: FC = () => {
  const modalContext = useContext(ModalProviderContext);
  if (modalContext.modals.length === 0) {
    return <></>;
  }
  const modalWidthMap = {
    md: "md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4",
    lg: "md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/3",
    xl: "md:w-full lg:w-2/3 xl:w-2/3 2xl:w-1/2",
    "2xl": "md:w-full lg:w-4/5 xl:w-3/4 2xl:w-2/3",
  };
  return (
    <>
      {modalContext.modals.map((modal, i) => {
        const width = modalWidthMap[modal.size ?? "md"];
        const isFirst = i === 0;
        const isCurrent = i === modalContext.modals.length - 1;
        return (
          <div
            className={`fixed inset-0 z-50 h-screen w-screen ${isFirst && "bg-[#000A]"} flex flex-col items-center justify-center`}
          >
            <div
              className={"fixed z-[-1] h-screen w-screen"}
              onClick={() => modalContext.close()}
            ></div>
            <div
              key={i}
              className={`h-full md:h-auto w-full ${width} bg-white rounded-sm shadow-sm ${isCurrent ? "" : "blur-lg opacity-25"}`}
            >
              <header
                className={"h-12 flex items-center justify-between px-4 border-b border-slate-100"}
              >
                <span className={"font-medium text-brand-900"}>{modal.title}</span>
                <button
                  className={"h-10 w-10 -mr-3 hover:bg-slate-100 rounded-sm"}
                  onClick={() => modalContext.close()}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </header>
              <main className={"max-h-[calc(100vh-8rem)] overflow-y-auto"}>
                <div className={"mx-4 mt-3"}>
                  {modal.body instanceof Function ? modal.body(modal.params) : modal.body}
                </div>
              </main>
            </div>
          </div>
        );
      })}
    </>
  );
};

export const ModalFooter: FC<{
  text: string;
  textSecondary?: string;
  icon?: IconDefinition;
  iconSecondary?: IconDefinition;
  disabled?: boolean;
  onSubmit?: () => void | Promise<void>;
  onSubmitSecondary?: () => void | Promise<void>;
}> = (props) => {
  const { close } = useContext(ModalProviderContext);
  const [isSaving, setIsSaving] = useState(false);
  const handleModalAction = useCallback(
    (callback?: () => void | Promise<void>) => {
      const result = callback?.();
      const promise = result instanceof Promise ? result : Promise.resolve();

      setIsSaving(true);
      promise
        .then(() => {
          setIsSaving(false);
          close();
        })
        .catch((e) => {
          console.warn("Modal promise rejected", e);
          setIsSaving(false);
        });
    },
    [close],
  );

  return (
    <footer
      className={
        "-mx-4 px-4 mt-3 py-3 border-t border-slate-100 flex items-center justify-between space-x-3"
      }
    >
      {!isSaving && (
        <div className={"flex gap-2"}>
          <Button
            submit={true}
            type={"primary"}
            size={"md"}
            text={props.text}
            icon={props.icon ?? faSave}
            onClick={() => handleModalAction(props.onSubmit)}
            disabled={props.disabled}
          />
          {props.onSubmitSecondary && props.textSecondary && (
            <Button
              submit={true}
              type={"primary"}
              size={"md"}
              text={props.textSecondary}
              icon={props.iconSecondary ?? faSave}
              onClick={() => handleModalAction(props.onSubmitSecondary)}
              disabled={props.disabled}
            />
          )}
        </div>
      )}

      {isSaving && (
        <div>
          <FontAwesomeIcon icon={faCircleNotch} spin={true} className={"text-blue-900 size-4"} />
        </div>
      )}

      <Button
        type={"secondary"}
        size={"md"}
        text={"Terug"}
        onClick={() => close()}
        disabled={isSaving}
      />
    </footer>
  );
};

export const useHasOpenModal = () => {
  const modalContext = useContext(ModalProviderContext);
  return modalContext.modals.length > 0;
};

export const useModalControls = () => {
  const modalContext = useContext(ModalProviderContext);
  return {
    close: modalContext.close,
  };
};
